/**
 * Application Settings Type Definitions
 */

// AI model configuration
export interface AIModelConfig {
  default: string
  fallback: string[]
}

// GPT Image 1.5 specific settings
export interface GPTImageSettings {
  quality: 'low' | 'medium' | 'high'
  resolution: '1024x1024' | '1024x1536' | '1536x1024'
  /**
   * 解像度ティア（1K/2K/4K）。設定時はアスペクト比に応じた実ピクセルへ解決され、
   * gpt-image-2 の生成サイズを決める（未設定時は '2K' 相当で扱う）。
   * resolution（固定px）より優先される。台本絵コンテ（喋る系含む）で使用。
   */
  sizeTier?: '1K' | '2K' | '4K'
}

// AI models settings
export interface AIModelsSettings {
  analysis: AIModelConfig
  generation: AIModelConfig
  backgroundRemoval: AIModelConfig // Background removal model
  text: AIModelConfig
  sceneDescription: AIModelConfig // Scene description generation model
  ocr: AIModelConfig // OCR and translation model
  smartMatching: AIModelConfig // Smart matching model (image-text pairing)
  musicRecommendation: AIModelConfig // Music recommendation model
  tasteRecommendation: AIModelConfig // Taste/style recommendation model
  voiceRecommendation?: AIModelConfig // Voice recommendation model (TTS, optional for backward compat)
  sfxRecommendation?: AIModelConfig // SFX recommendation model (optional for backward compat)
  problemImageGeneration?: AIModelConfig // Problem scene image generation model (PASONA P/A, optional for backward compat)
  agitationImageGeneration?: AIModelConfig // A（共感）シーン専用モデル
  fashionImageGeneration?: AIModelConfig // ファッション画像生成モデル
  dedicatedModelGeneration?: AIModelConfig // 専属モデルビルダー生成モデル
  gptImage: GPTImageSettings
  gptImageProblem?: GPTImageSettings // GPT Image settings for problem scene (PASONA P/A), defaults to gptImage if not set
  gptImageAgitation?: GPTImageSettings // GPT Image settings for agitation scene (PASONA A), defaults to gptImageProblem if not set
  gptImageFashion?: GPTImageSettings // GPT Image settings for fashion images, defaults to gptImage if not set
  featureImageGeneration?: AIModelConfig // 特徴訴求画像生成モデル（省略時は generation にフォールバック）
  gptImageFeature?: GPTImageSettings // 特徴訴求用 GPT Image 設定（省略時は gptImage にフォールバック）
  thumbnailImageGeneration?: AIModelConfig // サムネイル画像生成モデル（省略時は generation にフォールバック）
  gptImageThumbnail?: GPTImageSettings // サムネイル用 GPT Image 設定
  gptImageDedicated?: GPTImageSettings // 専属モデルビルダー用 GPT Image 設定
  gptImageStoryboard?: GPTImageSettings // 台本絵コンテ用 GPT Image 設定
}

// Character settings (for image generation)
export interface CharacterSettings {
  nationality: 'unspecified' | 'japanese' | 'korean'
  gender: 'unspecified' | 'female' | 'male'
  ageGroup: 'unspecified' | 'baby' | 'kids' | 'teens' | '20s' | '30s' | '40s' | '50s' | '60s' | '70s' | '80s'
  hairLength: 'unspecified' | 'short' | 'medium' | 'long'
  hairStyle: 'unspecified' | 'straight' | 'wavy' | 'curly' | 'perm' | 'bob' | 'short-bob' | 'braided' | 'braid' | 'bun' | 'ponytail' | 'twin-tails' | 'half-up' | 'side-tail' | 'updo' | 'wolf-cut' | 'outward-flip' | 'inward-curl' | 'undercut' | 'slicked-back' | 'spiky' | 'crew-cut' | 'flat-top' | 'side-part' | 'center-part' | 'mushroom-cut' | 'mohawk' | 'afro' | 'dreadlocks' | 'cornrows'
  hairColor: 'unspecified' | 'black' | 'dark-brown' | 'brown' | 'light-brown' | 'ash-brown' | 'beige' | 'greige' | 'pink-brown' | 'orange-brown' | 'red' | 'blonde' | 'silver-gray' | 'other'
  height: 'unspecified' | 'very-short' | 'short' | 'slightly-short' | 'average' | 'slightly-tall' | 'tall' | 'very-tall'
  bodyType: 'unspecified' | 'very-slim' | 'slim' | 'slightly-slim' | 'average' | 'slightly-curvy' | 'curvy' | 'voluptuous' | 'glamorous' | 'athletic' | 'muscular'
}

// Default character settings
export const DEFAULT_CHARACTER_SETTINGS: CharacterSettings = {
  nationality: 'japanese',      // Default to Japanese
  gender: 'unspecified',         // Others unspecified
  ageGroup: 'unspecified',
  hairLength: 'unspecified',
  hairStyle: 'unspecified',
  hairColor: 'unspecified',
  height: 'unspecified',
  bodyType: 'unspecified',
}

// Processing settings
export interface ProcessingSettings {
  whiteoutEnabled: boolean
  sceneImageCount: number // Per-analysis scene image cap (5-13)
  copyVariants: number // 1-10
  /** 素材生成の向き（9:16 / 16:9）。Gemini・GPT解像度・台本・Seedance と連動。最終出力の Template 向きとは別 */
  generationAspectRatio?: '9:16' | '16:9'
  geminiAspectRatio: '9:16' | '16:9' | '1:1' | '4:5' // Gemini画像生成のデフォルトアスペクト比
  geminiAspectRatioProblem?: '9:16' | '16:9' | '1:1' | '4:5' // 問題シーン画像（PASONAテンプレート）のアスペクト比
  geminiAspectRatioAgitation?: '9:16' | '16:9' | '1:1' | '4:5' // 共感シーン画像（PASONA A）のアスペクト比
  geminiAspectRatioFashion?: '9:16' | '16:9' | '1:1' | '4:5' // ファッション画像のアスペクト比
  geminiAspectRatioFeature?: '9:16' | '16:9' | '1:1' | '4:5' // 特徴訴求画像のアスペクト比
  autoStartAnalysis: boolean
  preAnalysisInfoEnabled: boolean // 事前詳細情報モード（デフォルト: false）
  characterSettings: CharacterSettings // Character appearance settings
  sceneImageRetention: {
    maxUsageScenes: number // Maximum usage scene images to keep (1-50, default: 50)
    maxFeatureScenes: number // Maximum feature appeal images to keep (1-50, default: 50)
    enableMerge: boolean // Enable merging existing and new images (default: true)
  }
  whiteoutImageUsage: {
    enableEvaluation: boolean // Enable whiteout image evaluation (default: true)
    topN: number // Number of top-scored whiteout images to prioritize (1-5, default: 2)
    minScore: number // Minimum score threshold for using whiteout images (0-100, default: 75)
  }
  // parallelProcessing: Deprecated but kept for backward compatibility
  parallelProcessing?: {
    enabled: boolean
    maxConcurrent: number // 1-5
  }
}

// Video encoding quality presets
export type VideoQualityPreset = 'draft' | 'standard' | 'high' | 'ultra' | 'custom'

// X264 encoding presets (speed vs compression efficiency)
export type X264Preset = 'ultrafast' | 'veryfast' | 'faster' | 'fast' | 'medium' | 'slow' | 'slower' | 'veryslow'

// Video codec options
export type VideoCodec = 'h264' | 'h265' | 'vp8' | 'vp9' | 'prores'

// Pixel format options (from @remotion/renderer)
export type PixelFormat = 'yuv420p' | 'yuv420p10le' | 'yuv422p' | 'yuv422p10le' | 'yuv444p' | 'yuv444p10le' | 'yuva420p' | 'yuva444p10le'

// Scene duration mode
export type SceneDurationMode = 'template' | 'tts' | 'video' | 'max'

// Video default settings
export interface VideoSettings {
  defaultAspectRatio: '9:16' | '16:9' | '1:1' | '4:5'
  defaultTemplate: string
  defaultFps: 24 | 30 | 60
  defaultQuality: 'standard' | 'high' // Legacy field for UI compatibility
  defaultSceneDurationMode?: SceneDurationMode
  finalizeModeEnabled?: boolean
  autoDownloadOnRenderComplete?: boolean
  // Advanced encoding settings
  encoding: {
    codec: VideoCodec
    qualityPreset: VideoQualityPreset
    crf: number | null // Constant Rate Factor (null = use preset default)
    x264Preset: X264Preset
    pixelFormat: PixelFormat
    videoBitrate?: string // e.g., '8M', '10M' (alternative to CRF for hardware acceleration)
  }
}

// UI settings
export interface UISettings {
  theme: 'dark' | 'light' | 'system'
  language: 'ja' | 'en'
  projectsView: 'grid' | 'list'
  showCostDisplay: boolean
  sidebarCollapsed: boolean
  /** 画面右下のサポートチャットを表示するか（既定: オフ） */
  showChatbot: boolean
}

// Cost settings
export interface CostSettings {
  usdToJpyRate: number
  warningThreshold: number | null // JPY, null = no warning
  monthlyBudget: number | null // JPY, null = no limit
}

// Image evaluation model configuration
export interface EvaluationModelConfig {
  provider: 'gemini' | 'openai'
  model: string
}

// Gradient stop for background
export interface GradientStop {
  offset: number // 0-1
  color: string // HEX color
}


// Per-scene style overrides (take precedence over taste preset)
export interface SceneStyleOverrides {
  // テキスト（既存）
  color?: string           // テキスト色。CTA では textColor にも適用
  fontSize?: number        // フォントサイズ (px)
  maxWidthPercent?: number // テキスト幅 (%)
  // テキスト（追加）
  fontFamily?: string      // フォントファミリー
  fontWeight?: number      // フォントウェイト (100/300/400/500/700/900)
  textShadow?: string      // テキストシャドウ CSS。'none'=明示なし、undefined=テイスト準拠
  // テキスト背景（追加）。CTA button の背景には適用しない
  backgroundColor?: string // テキスト背景色 (rgba)
  borderRadius?: number    // テキスト背景の角丸 (px)
  blur?: number            // テキスト背景のぼかし (px)
  padding?: string         // パディング CSS文字列 (e.g. "16px 24px")
  borderStyle?: BorderStyleSettings // フレームスタイル
}

// Dynamic scene settings (1-13 scenes)
export interface SceneSettingsItem {
  textPosition?: TextPosition  // optional: undefined = スタイル設定から取得
  textAnimation?: TextAnimation  // optional: undefined = headlineStyle/sellingPointStyle.animation にフォールバック
  animationDelay?: number // optional: undefined = headlineStyle/sellingPointStyle.animationDelay にフォールバック
  kenBurnsEffect: KenBurnsEffect
  imagePosition: ImagePositionSettings
  duration: number // seconds (1.0-10.0)
  sceneStyleOverrides?: SceneStyleOverrides
}

// Import SceneKey type
import type { SceneKey } from './project'
import { DEFAULT_TTS_SETTINGS } from './tts'

// Default scene settings generator
export function createDefaultSceneSettings(sceneCount: number = 5): Partial<Record<SceneKey, SceneSettingsItem>> {
  const settings: Partial<Record<SceneKey, SceneSettingsItem>> = {}

  for (let i = 1; i <= sceneCount; i++) {
    const sceneKey = `scene${i}` as SceneKey
    const isHeadline = i <= 2

    settings[sceneKey] = {
      textPosition: isHeadline ? { x: 'center', y: 50 } : { x: 'center', y: 70 },
      textAnimation: i === 1 ? 'none' : 'slideUp',   // scene1 はデフォルトで即時表示（サムネイル用）
      animationDelay: i === 1 ? 0 : (isHeadline ? 10 : 8),
      kenBurnsEffect: (['zoomIn', 'panRight', 'zoomInPanRight', 'panLeft', 'zoomOut'][(i - 1) % 5]) as KenBurnsEffect,
      imagePosition: {
        vertical: 'center center',
        horizontal: 'center center',
        square: 'center center',
        portrait: 'center center',
      },
      duration: 2.7,  // 全シーン統一（2.7秒）
    }
  }

  return settings
}

// Animation preset type
export type BrandAnimation =
  // クラシック
  | 'fade'        // フェードイン（シンプル）
  | 'scale'       // スケール拡大（最もよく使われる - デフォルト）
  | 'bounce'      // バウンスアニメーション（クリエイティブ）
  | 'pulse'       // パルスアニメーション（クリエイティブ）
  | 'none'        // アニメーションなし
  // フェード系
  | 'fadeScale'   // フェードスケール（小さく拡大）
  // スライド系
  | 'slideUp'     // 下からスライドアップ
  | 'slideDown'   // 上からスライドダウン
  | 'slideLeft'   // 右からスライド
  | 'slideRight'  // 左からスライド
  // ズーム系
  | 'zoom'        // ズームイン（小→大）
  | 'zoomOut'     // ズームアウト（大→小）
  | 'pop'         // ポップ（瞬間拡大）
  // バウンス・スプリング系
  | 'zoomBounce'  // ズームバウンス（弾む）
  | 'elastic'     // エラスティック（伸縮）
  | 'rubberBand'  // ラバーバンド（ゴム）
  // 回転・3D系
  | 'flip'        // フリップ（Y軸回転）
  | 'flipX'       // フリップX（X軸回転）
  | 'rotate'      // ローテート（回転ズーム）
  | 'spin'        // スピン（高速回転）
  // 特殊効果
  | 'shake'       // シェイク（振動）
  | 'wiggle'      // ウィグル（揺れ）
  | 'float'       // フロート（浮遊）
  | 'glow'        // グロー（発光）
  | 'shimmer'     // シマー（輝き）
  | 'glitch'      // グリッチエフェクト（クリエイティブ）

// Logo scale settings per aspect ratio
export interface LogoScaleSettings {
  vertical: number    // 9:16 (縦動画) のスケール (0-400%, 100% = 画面を満たすサイズ)
  horizontal: number  // 16:9 (横動画) のスケール (0-400%, 100% = 画面を満たすサイズ)
  square: number      // 1:1 (正方形) のスケール (0-400%, 100% = 画面を満たすサイズ)
  portrait: number    // 4:5 (縦型) のスケール (0-400%, 100% = 画面を満たすサイズ)
}

export const DEFAULT_LOGO_SCALE: LogoScaleSettings = {
  vertical: 100,
  horizontal: 100,
  square: 100,
  portrait: 100,
}

// Brand configuration
export interface BrandConfig {
  id: string // ブランドID (nanoid)
  name: string // ブランド名（必須）
  logoPath: string | null // ロゴ画像パス（オプション）
  textColor: string // テキスト色（デフォルト: #ffffff）
  fontFamily: string // フォント（デフォルト: Noto Sans JP）
  fontSize: number // フォントサイズ（後方互換性のため残す、デフォルト: 64）
  fontSizeVertical: number // 縦動画用フォントサイズ（9:16、デフォルト: 64）
  fontSizeHorizontal: number // 横動画用フォントサイズ（16:9、デフォルト: 84）
  fontSizeSquare: number // 正方形用フォントサイズ（1:1、デフォルト: 72）
  fontSize4x5: number // 4:5縦型用フォントサイズ（4:5、デフォルト: 68）
  logoScale?: LogoScaleSettings // ロゴスケール設定（アスペクト比別）
  // Background settings
  backgroundType: 'solid' | 'gradient' // 背景タイプ
  backgroundColor: string // 単色背景（solid時）
  gradientType: 'linear' | 'radial' // グラデーションタイプ
  gradientAngle: number // 角度（linear時、デフォルト: 135）
  gradientStops: GradientStop[] // グラデーションストップ
  duration: number // エンドカード表示秒数（デフォルト: 2.5秒）
  animation: BrandAnimation // アニメーションプリセット（デフォルト: scale）
  nameReading?: string // ブランド名のフリガナ（TTS読み上げ用・オプション）
  createdAt: string
  updatedAt: string
}

// Brand settings
export interface BrandSettings {
  brands: BrandConfig[]
  defaultBrandId: string | null
  useDefaultBrand: boolean // ON/OFF
}

// Dedicated model settings (default for new projects)
export interface DedicatedModelSettings {
  defaultModelId: string | null
  useDefaultModel: boolean
}

// Template customization types

// Ken Burns effect types
export type KenBurnsEffect =
  | 'zoomIn'
  | 'zoomOut'
  | 'panLeft'
  | 'panRight'
  | 'panUp'
  | 'panDown'
  | 'zoomInPanRight'
  | 'none'

// Text animation types (CTAAnimation の全24種 + テキスト固有の slideUp/scale を追加)
export type TextAnimation = CTAAnimation | 'slideUp' | 'scale'

// Text position types (detailed control)
export interface TextPosition {
  x: 'left' | 'center' | 'right' // Horizontal alignment
  y: number // Vertical position (0-100, percentage from top)
}

// Text position presets (for backward compatibility and quick selection)
export type TextPositionPreset = 'top' | 'top-center' | 'center' | 'bottom-center' | 'bottom'

// Helper function to convert preset to detailed position
export function presetToPosition(preset: TextPositionPreset): TextPosition {
  switch (preset) {
    case 'top':
      return { x: 'center', y: 10 }
    case 'top-center':
      return { x: 'center', y: 30 }
    case 'center':
      return { x: 'center', y: 50 }
    case 'bottom-center':
      return { x: 'center', y: 70 }
    case 'bottom':
      return { x: 'center', y: 85 }
    default:
      return { x: 'center', y: 50 }
  }
}

// Helper function to check if position is near a preset
export function positionToPreset(position: TextPosition): TextPositionPreset | null {
  if (position.x === 'center') {
    if (position.y <= 15) return 'top'
    if (position.y >= 25 && position.y <= 35) return 'top-center'
    if (position.y >= 45 && position.y <= 55) return 'center'
    if (position.y >= 65 && position.y <= 75) return 'bottom-center'
    if (position.y >= 80) return 'bottom'
  }
  return null
}

// CTA animation types
export type CTAAnimation =
  // 既存（カテゴリー0: クラシック）
  | 'bounce'       // スプリングバウンス
  | 'pulse'        // 連続拡大縮小
  | 'slide'        // 下から上スライド
  | 'none'         // アニメーションなし

  // カテゴリー1: フェード系
  | 'fade'         // シンプルフェードイン
  | 'fadeScale'    // フェード+小拡大

  // カテゴリー2: スライド系
  | 'slideLeft'    // 左からスライド
  | 'slideRight'   // 右からスライド
  | 'slideDown'    // 上からスライド

  // カテゴリー3: ズーム系
  | 'zoom'         // ズームイン
  | 'zoomOut'      // ズームアウト
  | 'pop'          // ポップ出現

  // カテゴリー4: バウンス・スプリング系
  | 'zoomBounce'   // ズーム+バウンス
  | 'elastic'      // エラスティック
  | 'rubberBand'   // ゴム伸縮

  // カテゴリー5: 回転・3D系
  | 'flip'         // Y軸フリップ
  | 'flipX'        // X軸フリップ
  | 'rotate'       // 回転ズーム
  | 'spin'         // 高速回転

  // カテゴリー6: 特殊効果
  | 'shake'        // 振動
  | 'wiggle'       // 揺れ
  | 'float'        // 浮遊
  | 'glow'         // 発光
  | 'shimmer'      // 輝き

// Border/Frame style types
export type BorderStyleType =
  | 'none'
  | 'rounded-box'
  | 'speech-bubble'
  | 'dotted'
  | 'dashed'
  | 'double'
  | 'glassmorphism'
  | 'neon-glow'

export type SpeechBubbleDirection = 'left' | 'right' | 'top' | 'bottom'

export interface BorderStyleSettings {
  type: BorderStyleType
  // Border properties (for dotted/dashed/double)
  borderWidth?: number // 1-10px
  borderColor?: string // hex color
  borderOpacity?: number // 0-1
  // Speech bubble specific
  bubbleDirection?: SpeechBubbleDirection
  bubbleTailSize?: number // 10-100px
  // Glassmorphism specific
  glassBlur?: number // 10-30px, backdrop blur intensity
  glassBorderColor?: string // hex color (usually light colors)
  glassBorderOpacity?: number // 0.1-0.5 (subtle border)
  glassShadow?: boolean // enable/disable soft shadow
  // Neon glow specific
  glowIntensity?: number // 0-20px
  glowColor?: string // hex color
}

export const DEFAULT_BORDER_STYLE: BorderStyleSettings = {
  type: 'rounded-box',
  borderWidth: 2,
  borderColor: '#ffffff',
  borderOpacity: 1.0,
  bubbleDirection: 'bottom',
  bubbleTailSize: 25,
  glassBlur: 20,
  glassBorderColor: '#ffffff',
  glassBorderOpacity: 0.3,
  glassShadow: true,
  glowIntensity: 10,
  glowColor: '#00ffff',
}

// Padding values (for visual padding controls)
export interface PaddingValues {
  top: number
  right: number
  bottom: number
  left: number
}

// Helper function to parse CSS padding string
export function parsePadding(paddingStr: string): PaddingValues {
  const parts = paddingStr.trim().split(/\s+/).map((p) => parseInt(p.replace('px', ''), 10) || 0)

  if (parts.length === 1) {
    return { top: parts[0], right: parts[0], bottom: parts[0], left: parts[0] }
  } else if (parts.length === 2) {
    return { top: parts[0], right: parts[1], bottom: parts[0], left: parts[1] }
  } else if (parts.length === 4) {
    return { top: parts[0], right: parts[1], bottom: parts[2], left: parts[3] }
  }

  return { top: 16, right: 24, bottom: 16, left: 24 }
}

// Helper function to format padding values to CSS string
export function formatPadding(padding: PaddingValues): string {
  if (padding.top === padding.right && padding.right === padding.bottom && padding.bottom === padding.left) {
    return `${padding.top}px`
  } else if (padding.top === padding.bottom && padding.right === padding.left) {
    return `${padding.top}px ${padding.right}px`
  } else {
    return `${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px`
  }
}

// Text shadow values (for visual shadow controls)
export interface TextShadowValues {
  offsetX: number // -20 to 20px
  offsetY: number // -20 to 20px
  blurRadius: number // 0 to 20px
  color: string // hex color
  opacity: number // 0 to 1
}

// Helper function to parse CSS text-shadow string
export function parseTextShadow(shadowStr: string): TextShadowValues {
  // Default values
  const defaultShadow: TextShadowValues = {
    offsetX: 0,
    offsetY: 2,
    blurRadius: 4,
    color: '#000000',
    opacity: 0.5,
  }

  if (!shadowStr || shadowStr.trim() === 'none' || shadowStr.trim() === '') {
    return defaultShadow
  }

  try {
    // Parse "0px 2px 4px rgba(0, 0, 0, 0.5)" or "0 2px 4px #000000"
    const rgbaMatch = shadowStr.match(/(-?\d+)(?:px)?\s+(-?\d+)(?:px)?\s+(-?\d+)(?:px)?\s+rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
    if (rgbaMatch) {
      const [, offsetX, offsetY, blurRadius, r, g, b, a] = rgbaMatch
      const color = `#${Number(r).toString(16).padStart(2, '0')}${Number(g).toString(16).padStart(2, '0')}${Number(b).toString(16).padStart(2, '0')}`
      return {
        offsetX: parseInt(offsetX, 10),
        offsetY: parseInt(offsetY, 10),
        blurRadius: parseInt(blurRadius, 10),
        color,
        opacity: a ? parseFloat(a) : 1,
      }
    }

    // Parse "0px 2px 4px #000000"
    const hexMatch = shadowStr.match(/(-?\d+)(?:px)?\s+(-?\d+)(?:px)?\s+(-?\d+)(?:px)?\s+(#[0-9a-fA-F]{6})/)
    if (hexMatch) {
      const [, offsetX, offsetY, blurRadius, color] = hexMatch
      return {
        offsetX: parseInt(offsetX, 10),
        offsetY: parseInt(offsetY, 10),
        blurRadius: parseInt(blurRadius, 10),
        color,
        opacity: 1,
      }
    }

    return defaultShadow
  } catch {
    return defaultShadow
  }
}

// Helper function to format text shadow values to CSS string
export function formatTextShadow(shadow: TextShadowValues): string {
  const r = parseInt(shadow.color.slice(1, 3), 16)
  const g = parseInt(shadow.color.slice(3, 5), 16)
  const b = parseInt(shadow.color.slice(5, 7), 16)
  return `${shadow.offsetX}px ${shadow.offsetY}px ${shadow.blurRadius}px rgba(${r}, ${g}, ${b}, ${shadow.opacity})`
}

// Transition types
export type TransitionType =
  | 'none'
  | 'fade'
  | 'slide'
  | 'wipe'
  | 'flip'
  | 'clockWipe'
  | 'iris'
  | 'cube'

export interface BaseTransition {
  type: TransitionType
  duration: number // frames (0-30)
}

export interface FadeTransition extends BaseTransition {
  type: 'fade'
}

export interface SlideTransition extends BaseTransition {
  type: 'slide'
  direction: 'up' | 'down' | 'left' | 'right'
  easing: 'linear' | 'ease-in' | 'ease-out' | 'ease-in-out'
}

export interface WipeTransition extends BaseTransition {
  type: 'wipe'
  direction: 'left' | 'right' | 'up' | 'down'
  softness: number // 0-100
}

export interface FlipTransition extends BaseTransition {
  type: 'flip'
  direction: 'horizontal' | 'vertical'
  perspective: number // 500-2000
}

export interface ClockWipeTransition extends BaseTransition {
  type: 'clockWipe'
  clockwise: boolean
  startAngle: number // 0-360
}

export interface IrisTransition extends BaseTransition {
  type: 'iris'
  shape: 'circle' | 'diamond' | 'star'
  centerX: number // 0-100 (percentage)
  centerY: number // 0-100 (percentage)
}

export interface CubeTransition extends BaseTransition {
  type: 'cube'
  direction: 'left' | 'right' | 'up' | 'down'
  perspective: number // 500-2000
}

export type Transition =
  | { type: 'none' }
  | FadeTransition
  | SlideTransition
  | WipeTransition
  | FlipTransition
  | ClockWipeTransition
  | IrisTransition
  | CubeTransition

// Base text style (shared by headline and selling point)
export interface BaseTextStyle {
  color: string // Text color (hex)
  fontFamily: string // Font name
  fontWeight?: number // Font weight (100-900, default: headline=700, sellingPoint=400)
  backgroundColor: string // Background color (rgba)
  padding: string // CSS padding
  borderRadius: number // Border radius (0-50px)
  textShadow: string // CSS text-shadow
  blur: number // Background blur (0-20px)
  maxWidthPercent?: number // Maximum width as percentage of screen (20-100%, font-size independent)
  borderStyle?: BorderStyleSettings // NEW: Optional for backward compatibility
  textOpacity?: number // テキスト透明度 (0.0-1.0, default: 1.0) - 0=非表示(TTS用), 1=完全表示
  animation?: TextAnimation // デフォルトアニメーション種類（シーン別設定のフォールバック）
  animationDelay?: number // アニメーション遅延フレーム数 (0-60, default: 10)
}

// Headline style (Scene 1, 2)
export interface HeadlineStyle extends BaseTextStyle {
  position?: TextPosition // NEW: Position for headline text (default: center)
}

// Selling point style (Scene 3, 4, 5)
export interface SellingPointStyle extends BaseTextStyle {
  position?: TextPosition // NEW: Position for selling point text (default: bottom)
}

// CTA button style (all scenes)
// Note: fontSize is managed in fontSizes object, not here
export interface CTAStyle {
  backgroundColor: string // Button background color (hex or rgba)
  textColor: string // Button text color (hex)
  borderRadius: number // Border radius (0-50px)
  animation: CTAAnimation
  padding?: string // Optional: default is '16px 48px'
  textShadow?: string // Optional: default is '0 2px 4px rgba(0, 0, 0, 0.3)'
  fontWeight?: number // Font weight (100-900, default: 700)
  fontFamily?: string // Font family (default: 'Noto Sans JP')
  position?: TextPosition // NEW: Position for CTA button (default: bottom)
  borderStyle?: BorderStyleSettings // NEW: Optional frame style for CTA
  textOpacity?: number // テキスト透明度 (0.0-1.0, default: 1.0) - 0=非表示(TTS用), 1=完全表示
}

/**
 * Image position type
 * Supports preset positions (5x5 grid) and custom percentage values
 */
export type ImagePosition =
  // 中央列（既存の9つの位置との互換性維持）
  | 'center center'     // デフォルト
  | 'center top'        // 上寄せ
  | 'center bottom'     // 下寄せ
  | 'left center'       // 左寄せ
  | 'right center'      // 右寄せ
  | 'left top'          // 左上
  | 'right top'         // 右上
  | 'left bottom'       // 左下
  | 'right bottom'      // 右下
  // 5x5グリッド追加位置（16個）
  // 上段の中間位置
  | 'center-left top'
  | 'center-right top'
  // 上中段（center-top）
  | 'left center-top'
  | 'center-left center-top'
  | 'center center-top'
  | 'center-right center-top'
  | 'right center-top'
  // 中段の中間位置
  | 'center-left center'
  | 'center-right center'
  // 下中段（center-bottom）
  | 'left center-bottom'
  | 'center-left center-bottom'
  | 'center center-bottom'
  | 'center-right center-bottom'
  | 'right center-bottom'
  // 下段の中間位置
  | 'center-left bottom'
  | 'center-right bottom'
  | string              // カスタム値（例: '30% 70%'）

/**
 * Image position settings for all aspect ratios
 */
export interface ImagePositionSettings {
  vertical: ImagePosition    // 9:16
  horizontal: ImagePosition  // 16:9
  square: ImagePosition      // 1:1
  portrait: ImagePosition    // 4:5
}

// SimpleSlide template settings
export interface SimpleSlideTemplateSettings {
  transitions: {
    sceneIn: Transition
    sceneOut: Transition
    skipFirstSceneIn?: boolean        // NEW: 最初のシーンのインをスキップ
    skipLastSceneOut?: boolean        // NEW: 最後のシーンのアウトをスキップ（ブランドカードへのスムーズな切り替え）
    transitionOverlap?: number        // NEW: トランジション重複時間（フレーム数）
  }
  // DEPRECATED: Kept for backward compatibility and migration fallback
  // After migration, this field is preserved but new styles take precedence
  text?: BaseTextStyle
  // NEW: Three independent styles (required after migration)
  headlineStyle: HeadlineStyle
  sellingPointStyle: SellingPointStyle
  ctaStyle: CTAStyle

  // === DEPRECATED: Legacy fields (後方互換性のため保持) ===
  textPositions?: {
    scene1: TextPosition
    scene2: TextPosition
    scene3: TextPosition
    scene4: TextPosition
    scene5: TextPosition
  }

  // PASONAフェーズ別テキストスタイル（StorySlide専用。未設定時は getTextStyleForScene でフォールバック）
  pasonaAttentionStyle?: HeadlineStyle
  pasonaAgitationStyle?: HeadlineStyle
  pasonaSolutionStyle?: HeadlineStyle
  pasonaOfferStyle?: HeadlineStyle
  pasonaNarrowingStyle?: HeadlineStyle

  // === Dynamic scene settings (1-13 scenes) ===
  sceneSettings: Partial<Record<SceneKey, SceneSettingsItem>>

  mainBackgroundColor: string // hex color
  fontSizes: {
    headlineVertical: number // 9:16 (TikTok)
    headlineHorizontal: number // 16:9 (YouTube)
    headlineSquare: number // 1:1 (Instagram)
    headlinePortrait: number // 4:5 (Feed)
    sellingPointVertical: number
    sellingPointHorizontal: number
    sellingPointSquare: number
    sellingPointPortrait: number
    ctaVertical: number
    ctaHorizontal: number
    ctaSquare: number
    ctaPortrait: number
  }
}


// Template settings (supports multiple templates)
export interface TemplateSettings {
  selectedTemplate: string // 'simpleSlide' | 'modernSlide' | ...
  simpleSlide: SimpleSlideTemplateSettings
  // Future templates can be added here:
  // modernSlide?: ModernSlideTemplateSettings
  // minimalistSlide?: MinimalistSlideTemplateSettings
}

// Default SimpleSlide settings (matches current hardcoded values)
export const DEFAULT_SIMPLE_SLIDE_SETTINGS: SimpleSlideTemplateSettings = {
  transitions: {
    sceneIn: { type: 'fade', duration: 6 },      // 8 → 6
    sceneOut: { type: 'fade', duration: 6 },     // 5 → 6
    skipFirstSceneIn: true,                      // NEW: 最初のシーンのインをスキップ（即座に開始）
    skipLastSceneOut: true,                      // NEW: 最後のシーンのアウトをスキップ（ブランドカードへスムーズに）
    transitionOverlap: 4,                        // NEW: Fadeに最適な値
  },
  // Kept for backward compatibility
  text: {
    color: '#ffffff',
    fontFamily: 'Noto Sans JP',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    padding: '16px 24px',
    borderRadius: 8,
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
    blur: 4,
  },
  // NEW: Three independent styles
  headlineStyle: {
    color: '#ffffff',
    fontFamily: 'Noto Sans JP',
    backgroundColor: 'rgba(0, 0, 0, 0.65)', // Darker background
    padding: '20px 28px', // More padding
    borderRadius: 12, // More rounded
    textShadow: '0 3px 6px rgba(0, 0, 0, 0.7)', // Stronger shadow
    blur: 6, // Stronger blur
    position: { x: 'center', y: 50 }, // NEW: Headline text at center (detailed control)
    maxWidthPercent: 70, // 70% of screen width (standard)
    borderStyle: DEFAULT_BORDER_STYLE, // NEW: Frame style settings
    animation: 'none' as TextAnimation,
    animationDelay: 0,
  },
  sellingPointStyle: {
    color: '#ffffff',
    fontFamily: 'Noto Sans JP',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    padding: '16px 24px',
    borderRadius: 8,
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
    blur: 4,
    position: { x: 'center', y: 50 }, // NEW: Selling point text at center (detailed control)
    maxWidthPercent: 60, // 60% of screen width (narrow)
    borderStyle: DEFAULT_BORDER_STYLE, // NEW: Frame style settings
    animation: 'none' as TextAnimation,
    animationDelay: 0,
  },
  ctaStyle: {
    backgroundColor: '#0ea5e9', // Sky blue
    textColor: '#ffffff',
    borderRadius: 50, // Fully rounded (pill shape)
    animation: 'bounce',
    padding: '20px 60px',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    position: { x: 'center', y: 70 }, // NEW: CTA button at bottom-center (detailed control)
    borderStyle: DEFAULT_BORDER_STYLE, // NEW: Frame style settings
  },
  // Scene settings (unified format)
  sceneSettings: {
    scene1: {
      textPosition: { x: 'center', y: 50 },
      textAnimation: 'none',
      animationDelay: 0,
      kenBurnsEffect: 'zoomIn',
      imagePosition: {
        vertical: 'center center',
        horizontal: 'center center',
        square: 'center center',
        portrait: 'center center',
      },
      duration: 2.7,
    },
    scene2: {
      textPosition: { x: 'center', y: 50 },
      textAnimation: 'slideUp',
      animationDelay: 10,
      kenBurnsEffect: 'panRight',
      imagePosition: {
        vertical: 'center center',
        horizontal: 'center center',
        square: 'center center',
        portrait: 'center center',
      },
      duration: 2.7,
    },
    scene3: {
      textPosition: { x: 'center', y: 70 },
      textAnimation: 'slideUp',
      animationDelay: 8,
      kenBurnsEffect: 'zoomInPanRight',
      imagePosition: {
        vertical: 'center center',
        horizontal: 'center center',
        square: 'center center',
        portrait: 'center center',
      },
      duration: 2.7,
    },
    scene4: {
      textPosition: { x: 'center', y: 70 },
      textAnimation: 'slideUp',
      animationDelay: 8,
      kenBurnsEffect: 'panLeft',
      imagePosition: {
        vertical: 'center center',
        horizontal: 'center center',
        square: 'center center',
        portrait: 'center center',
      },
      duration: 2.7,
    },
    scene5: {
      textPosition: { x: 'center', y: 70 },
      textAnimation: 'slideUp',
      animationDelay: 8,
      kenBurnsEffect: 'panLeft',
      imagePosition: {
        vertical: 'center center',
        horizontal: 'center center',
        square: 'center center',
        portrait: 'center center',
      },
      duration: 2.7,
    },
  },
  mainBackgroundColor: '#000000',
  fontSizes: {
    headlineVertical: 76,
    headlineHorizontal: 84,
    headlineSquare: 84,
    headlinePortrait: 76,
    sellingPointVertical: 52,
    sellingPointHorizontal: 60,
    sellingPointSquare: 60,
    sellingPointPortrait: 52,
    ctaVertical: 70,
    ctaHorizontal: 72,
    ctaSquare: 72,
    ctaPortrait: 70,
  },
}

// Default template settings
export const DEFAULT_TEMPLATE_SETTINGS: TemplateSettings = {
  selectedTemplate: 'simpleSlide',
  simpleSlide: DEFAULT_SIMPLE_SLIDE_SETTINGS,
}

// AI Video Generation Settings
export interface VideoGenerationSettings {
  enabled: boolean
  model:
    | 'seedance-1.5-pro' | 'seedance-1.0-lite'
    | 'seedance-2.0'
    | 'grok-imagine' | 'kling'
    | 'pixverse-v6'
    | 'veo-3.1-lite'
    | 'gemini-omni-1.1-flash'  // veoParams を共用（Veo 3.1 Lite の後継）
  duration: number
  applyToRanks: number[]
  durationHandling: 'tts' | 'video'
  seedanceParams: {
    resolution: '480p' | '720p' | '1080p'
    aspectRatio: '9:16' | '16:9' | '4:3' | '1:1' | '3:4' | '21:9' | 'auto'
    generateAudio: boolean
    cameraFixed: boolean
  }
  klingParams: {
    version: 'v2.6' | 'v3.0'
    negativePrompt: string
    generateAudio: boolean
  }
  grokParams: {
    aspectRatio: '1:1' | '16:9' | '9:16' | '4:3' | '3:4'
  }
  pixverseParams: {
    resolution: '360p' | '540p' | '720p' | '1080p'
    aspectRatio: '9:16' | '16:9' | '1:1' | '4:3' | '3:4'
    generateAudio: boolean
  }
  veoParams: {
    resolution: '720p' | '1080p'
    aspectRatio: '9:16' | '16:9'
  }
  prompts?: {
    usageScene: string      // 利用シーン画像用プロンプト（rank 1-3, 5+）
    featureAppeal: string   // 特徴訴求画像用プロンプト（rank 4）
    problemScene: string    // PASONA問題シーン用（generateVideosForImageList）
  }
}

export const DEFAULT_VIDEO_GENERATION_SETTINGS: VideoGenerationSettings = {
  enabled: true,
  model: 'seedance-1.5-pro',
  duration: 4,
  applyToRanks: [1, 2, 3, 4, 5],
  durationHandling: 'tts',
  seedanceParams: {
    resolution: '480p',
    aspectRatio: '9:16',
    generateAudio: false,
    cameraFixed: true,
  },
  klingParams: {
    version: 'v2.6',
    negativePrompt: 'blur, distort, and low quality',
    generateAudio: false,
  },
  grokParams: {
    aspectRatio: '16:9',
  },
  pixverseParams: {
    resolution: '720p',
    aspectRatio: '9:16',
    generateAudio: false,
  },
  veoParams: {
    resolution: '720p',
    aspectRatio: '9:16',
  },
  prompts: {
    usageScene: 'The scene gently comes to life with natural subtle movements and soft ambient light shifts. Slow cinematic camera drift, smooth and stable.',
    featureAppeal: 'Product stays perfectly still. Subtle natural lighting shift only. Slow elegant push-in camera movement. No product movement whatsoever.',
    problemScene: 'The scene gently comes to life with natural subtle movements and soft ambient light shifts. Slow cinematic camera drift, smooth and stable.',
  },
}

// Template generation settings (per-template ON/OFF)
export interface TemplateGenerationSettings {
  simpleSlide?: {             // undefined は後方互換で true 扱い
    enabled: boolean          // ON/OFF（デフォルト: true）
    generateAIVideos?: boolean // AI動画も生成するか（videoGeneration.enabled が true の場合のみ有効、デフォルト: false）
    usageSceneBuffer?: number  // 利用シーン画像バッファ枚数（0-5、デフォルト: 2）
    featureSceneBuffer?: number // 特徴訴求画像バッファ枚数（0-3、デフォルト: 0）
  }
  storySlide: {
    enabled: boolean          // PASONAテキスト + 問題シーン画像生成 ON/OFF（デフォルト: true）
    generateAIVideos: boolean // AI動画も生成するか（videoGeneration.enabled が true の場合のみ有効、デフォルト: false）
    usageSceneBuffer?: number  // 利用シーン画像バッファ枚数（0-3、デフォルト: 0）
  }
  fashionSlide?: {
    enabled: boolean          // ファッション画像生成 ON/OFF（デフォルト: false）
    generateAIVideos: boolean // AI動画も生成するか（videoGeneration.enabled が true の場合のみ有効、デフォルト: false）
  }
  storyboardSlide?: {
    enabled: boolean          // StoryboardSlide ON/OFF（デフォルト: true）
    generateAIVideos?: boolean // AI動画クリップも生成するか（デフォルト: false）
  }
}

// BGM設定（テンプレートファミリー別）
export interface BGMFamilySettings {
  musicStartScene: number  // 1-13、デフォルト1
}

export interface BGMSettings {
  SimpleSlide: BGMFamilySettings
  StorySlide: BGMFamilySettings
  FashionSlide?: BGMFamilySettings
  StoryboardSlide?: BGMFamilySettings
  disabledBgmIds?: string[]  // OFFにされたBGM ID（デフォルト: [] = 全て有効）
}

// SFX設定（テンプレートファミリー × シーン別）
export interface SFXFamilySettings {
  scenes: Partial<Record<string, boolean>>  // 'scene1': true/false
}

export interface SFXSettings {
  SimpleSlide: SFXFamilySettings
  StorySlide: SFXFamilySettings
  FashionSlide?: SFXFamilySettings
  StoryboardSlide?: SFXFamilySettings
  disabledSfxIds?: string[]  // OFFにされたSFX ID（デフォルト: [] = 全て有効）
  defaultSfxVolume?: number  // デフォルト音量 0-100%（デフォルト: 80）
}

// Text generation character limits
export interface CharLimitRange {
  min: number
  max: number
}

export interface TextGenerationSettings {
  headline: CharLimitRange       // デフォルト: { min: 10, max: 15 }
  sellingPoint: CharLimitRange   // デフォルト: { min: 10, max: 15 }
  cta: CharLimitRange            // デフォルト: { min: 5, max: 10 }
  pasonaChars: {
    p: number  // P（問題提起）
    a: number  // A（親近・共感。内部キーは後方互換）
    s: number  // S（解決策）
    o: number  // O（提案）
    n: number  // N（対象の絞り込み）
  }
}

export const DEFAULT_TEXT_GENERATION_SETTINGS: TextGenerationSettings = {
  headline: { min: 10, max: 15 },
  sellingPoint: { min: 10, max: 15 },
  cta: { min: 5, max: 10 },
  pasonaChars: { p: 15, a: 15, s: 15, o: 15, n: 15 },
}

// Storyboard settings
export interface StoryboardSettings {
  enabled: boolean
  defaultTaste: import('./storyboard').StoryboardTaste
  confirmationMode: boolean           // false = フルオート
  sceneCount: number                  // 4-10（デフォルト4）
  imageModel: AIModelConfig           // 絵コンテ画像生成モデル
  videoModel: 'seedance-1.5-pro' | 'seedance-1.0-lite' | 'seedance-2.0'
  videoDuration: number               // 4-10（デフォルト5）
  videoResolution: '480p' | '720p'    // ※Seedanceは1080p未対応
  lipSyncModel?: 'infinitetalk' | 'longcat-avatar-1.5'  // 喋るテンプレートの口パクモデル（WaveSpeed）。未設定は infinitetalk
  aspectRatio: '9:16' | '16:9' | '1:1'
  generateAudio: boolean              // Seedance: 音声生成（料金2倍）
  cameraFixed: boolean                // Seedance: カメラ固定
  videoClipMode?: 'single' | 'transition'  // 動画クリップ生成モード（デフォルト: 'single'）
  generateAIVideos?: boolean           // AI動画を生成するか（false=スライドショーモード、デフォルト: true）
  draftModel?: string                 // 台本生成モデル（未設定時: 'gemini-3.5-flash-lite'）
  evaluationModel?: string            // 台本評価モデル（未設定時/空文字=評価スキップ）
  styleInputs?: {                     // スタイル別の動的入力（アップロード画面 extraInputs 由来）
    userReviewText?: string           // 試してみたVlog: 実レビュー・感想（ナレーション/セリフの素材）
    userFaqText?: string              // よくあるQ&A / 喋るQ&A: Q:/A:/--- 形式の質問と答え
    speakQa?: string                  // 喋るQ&A: '1' なら口でも質問を読む（既定=答えのみ）
    faqQuestionCount?: string         // 喋るQ&A: 質問数 '2'..'6'
    faqPackSize?: string              // 喋るQ&A場面切替: '1'|'2'|'3'|'auto'（1シーンにまとめる問数）
    userHowtoText?: string            // 使い方デモ（喋る）: STEP:/--- 形式の手順
    howtoStepCount?: string           // 使い方デモ（喋る）: 手順数 '1'..'5'
    vlogSceneCount?: string           // 試してみたVlog（喋る）: シーン数 '2'..'5'（既定3）
    userBenefitText?: string          // ベネフィット訴求（喋る）: POINT:/--- 形式のセールスポイント（各1シーン）
    benefitPointCount?: string        // ベネフィット訴求（喋る）: セールスポイント数 '2'..'5'（既定3）
    userGiftTarget?: string           // ギフト提案: 贈る相手・シーン（例「母の日に、母へ」）
    userGiftShop?: string             // ギフト提案: 締めのお店・店員の雰囲気（例「ナチュラルな雑貨店の女性店員」）
    userCreativeBrief?: string        // シネマティック: 作りたいミニ映画の創作ブリーフ（例「怪獣が街に現れ、商品の力で撃退する特撮CM」）
    voiceId?: string                  // 喋る系（口パク）: 主人公の声。VoiceMetadata.id（例 'gm-leda'）。空/'auto'=おまかせ（AIが性別で自動選択）。WaveSpeed生成前に確定が必要
    sceneLocation?: string            // 喋る系（口パク）: 撮影場所。SceneLocation.id（例 'room'）。空/'auto'=おまかせ（商品に合う場所をAIが自動選択）
  }
}

// Thumbnail intro (hook video) settings
export interface ThumbnailSettings {
  enabled: boolean        // デフォルト: false
  duration: number        // 秒数、デフォルト: 2
  sfxEnabled: boolean     // SFX使用、デフォルト: true
  generateAiImageEnabled?: boolean // AI画像自動生成（デフォルト: true）
  ttsSpeed?: number       // TTS再生速度（デフォルト: 1.2）
  maxTextChars?: number   // フックテキスト最大文字数（デフォルト: 11）
  bgmInThumbnail?: boolean // サムネイル中もBGMを流す（デフォルト: false）
  useDedicatedModel?: boolean // 専属モデルをリファレンスとして使用（デフォルト: true）
  autoExtendDuration?: boolean // ナレーション音声に合わせて自動延長（デフォルト: true）
  autoExtendAdjustment?: number // 自動延長後の微調整秒数（-2.0〜+2.0、デフォルト: 0）
  ttsMatchDuration?: boolean  // TTS音声の長さに完全一致（表示時間・自動延長を無視）（デフォルト: true）
  ttsMatchAdjustment?: number // ttsMatchDuration ON 時の微調整秒数（-2.0〜+2.0、デフォルト: 0）
}

// Complete app settings
export interface AppSettings {
  aiModels: AIModelsSettings
  processing: ProcessingSettings
  video: VideoSettings
  ui: UISettings
  cost: CostSettings
  evaluationModel?: EvaluationModelConfig
  branding: BrandSettings
  template: TemplateSettings
  tts?: import('./tts').TTSSettings
  videoGeneration?: VideoGenerationSettings
  templateGeneration?: TemplateGenerationSettings
  bgm?: BGMSettings
  sfx?: SFXSettings
  textGeneration?: TextGenerationSettings
  dedicatedModel?: DedicatedModelSettings
  storyboard?: StoryboardSettings
  thumbnail?: ThumbnailSettings
  systemPrompts?: import('./prompts').SystemPromptsSettings
  // アカウント別テイストプリセット override
  // signup 時に admin マスター (globalSettings.tastePresetOverrides) からスナップショットコピーされ、以後独立
  tastePresetOverrides?: Record<string, Partial<TastePresetSettings>>
}

// Default settings
export const DEFAULT_SETTINGS: AppSettings = {
  aiModels: {
    analysis: {
      default: 'gemini-3.5-flash-lite',
      fallback: ['gemini-3.1-flash-lite', 'gpt-5-nano'],
    },
    generation: {
      default: 'gpt-image-2.5-sunburst',
      fallback: ['gemini-nano-banana-2.1', 'gemini-3-pro-image', 'gpt-image-2.5-flare'],
    },
    backgroundRemoval: {
      default: 'bria-rmbg',
      fallback: ['birefnet', 'rembg'],
    },
    text: {
      default: 'gemini-3.5-flash-lite',
      fallback: ['gemini-3.1-flash-lite', 'gpt-5-nano'],
    },
    sceneDescription: {
      default: 'gemini-3.5-flash-lite',
      fallback: ['gemini-3.1-flash-lite', 'gemini-2.5-pro'],
    },
    ocr: {
      default: 'gemini-3.5-flash-lite',
      fallback: ['gemini-3.1-flash-lite', 'gemini-2.5-pro'],
    },
    smartMatching: {
      default: 'gemini-2.5-flash-lite',
      fallback: ['gemini-2.5-flash', 'gemini-2.5-pro'],
    },
    musicRecommendation: {
      default: 'gemini-2.5-flash-lite',
      fallback: ['gemini-2.5-flash', 'gemini-2.5-pro'],
    },
    tasteRecommendation: {
      default: 'gemini-2.5-flash-lite',
      fallback: ['gemini-2.5-flash', 'gemini-2.5-pro'],
    },
    voiceRecommendation: {
      default: 'gemini-2.5-flash-lite',
      fallback: ['gemini-2.5-flash', 'gpt-4o-mini'],
    },
    sfxRecommendation: {
      default: 'gemini-2.5-flash-lite',
      fallback: ['gemini-2.5-flash', 'gemini-2.5-pro'],
    },
    problemImageGeneration: {
      default: 'gpt-image-2.5-sunburst',
      fallback: ['gemini-nano-banana-2.1', 'gemini-3-pro-image', 'gpt-image-2.5-flare'],
    },
    gptImage: {
      quality: 'medium',
      resolution: '1024x1536',
    },
    gptImageProblem: {
      quality: 'medium',
      resolution: '1024x1536',
    },
    agitationImageGeneration: {
      default: 'gpt-image-2.5-sunburst',
      fallback: ['gemini-nano-banana-2.1', 'gemini-3-pro-image', 'gpt-image-2.5-flare'],
    },
    gptImageAgitation: {
      quality: 'medium',
      resolution: '1024x1536',
    },
    fashionImageGeneration: {
      default: 'gpt-image-2.5-sunburst',
      fallback: ['gemini-nano-banana-2.1', 'gemini-3-pro-image', 'gpt-image-2.5-flare'],
    },
    gptImageFashion: {
      quality: 'medium',
      resolution: '1024x1536',
    },
    dedicatedModelGeneration: {
      default: 'gpt-image-2.5-sunburst',
      fallback: ['gemini-nano-banana-2.1', 'gemini-3-pro-image', 'gpt-image-2.5-flare'],
    },
    thumbnailImageGeneration: {
      default: 'gpt-image-2.5-sunburst',
      fallback: ['gemini-nano-banana-2.1', 'gemini-3-pro-image', 'gpt-image-2.5-flare'],
    },
    featureImageGeneration: {
      default: 'gpt-image-2.5-sunburst',
      fallback: ['gemini-nano-banana-2.1', 'gemini-3-pro-image', 'gpt-image-2.5-flare'],
    },
    gptImageFeature: {
      quality: 'medium',
      resolution: '1024x1536',
    },
    gptImageThumbnail: {
      quality: 'medium',
      resolution: '1024x1536',
    },
    gptImageDedicated: {
      quality: 'medium',
      resolution: '1024x1536',
    },
    gptImageStoryboard: {
      quality: 'medium',
      resolution: '1024x1536',
      sizeTier: '2K',
    },
  },
  processing: {
    whiteoutEnabled: true,
    sceneImageCount: 13, // 7 → 13に変更（複数画像アップロード時に全バリエーションを生成）
    copyVariants: 5,
    generationAspectRatio: '9:16',
    geminiAspectRatio: '9:16',
    geminiAspectRatioProblem: '9:16',
    geminiAspectRatioAgitation: '9:16',
    geminiAspectRatioFashion: '9:16',
    geminiAspectRatioFeature: '9:16',
    autoStartAnalysis: false,
    preAnalysisInfoEnabled: false,
    characterSettings: DEFAULT_CHARACTER_SETTINGS,
    sceneImageRetention: {
      maxUsageScenes: 50,
      maxFeatureScenes: 50,
      enableMerge: true,
    },
    whiteoutImageUsage: {
      enableEvaluation: true,
      topN: 2,
      minScore: 75,
    },
    // parallelProcessing moved to GlobalSettings
  },
  video: {
    defaultAspectRatio: '9:16',
    defaultTemplate: 'simple-slide',
    defaultFps: 30,
    defaultQuality: 'high', // Legacy field
    finalizeModeEnabled: false,
    autoDownloadOnRenderComplete: true,
    encoding: {
      codec: 'h264',
      qualityPreset: 'high',
      crf: null, // null = use preset default
      x264Preset: 'medium',
      pixelFormat: 'yuv420p',
    },
  },
  ui: {
    theme: 'light',
    language: 'ja',
    projectsView: 'grid',
    showCostDisplay: true,
    sidebarCollapsed: false,
    showChatbot: false,
  },
  cost: {
    usdToJpyRate: 155.91,
    warningThreshold: null,
    monthlyBudget: null,
  },
  evaluationModel: {
    provider: 'gemini',
    model: 'gemini-3.5-flash-lite',
  },
  branding: {
    brands: [],
    defaultBrandId: null,
    useDefaultBrand: true,
  },
  template: DEFAULT_TEMPLATE_SETTINGS,
  tts: DEFAULT_TTS_SETTINGS,
  videoGeneration: DEFAULT_VIDEO_GENERATION_SETTINGS,
  templateGeneration: {
    simpleSlide: { enabled: true, generateAIVideos: true, usageSceneBuffer: 0, featureSceneBuffer: 0 },
    storySlide: {
      enabled: true,
      generateAIVideos: true,
    },
    fashionSlide: {
      enabled: false,
      generateAIVideos: false,
    },
    storyboardSlide: {
      enabled: true,
      generateAIVideos: false,
    },
  },
  bgm: {
    SimpleSlide: { musicStartScene: 1 },
    StorySlide:  { musicStartScene: 3 },       // シーン3（A: 共感・親近感）から開始
    FashionSlide: { musicStartScene: 1 },
    StoryboardSlide: { musicStartScene: 1 },
    disabledBgmIds: [],
  },
  sfx: {
    SimpleSlide: { scenes: { scene1: true, scene2: true } },         // シーン1・2をON
    StorySlide:  { scenes: { scene1: true, scene2: true } },        // シーン1（P: 問題提起）+ シーン2（A: 共感）をON
    FashionSlide: { scenes: {} },                                    // 全シーンOFF
    StoryboardSlide: { scenes: {} },                                 // 全シーンOFF
    disabledSfxIds: [],                        // 全て有効（デフォルト）
    defaultSfxVolume: 80,                      // デフォルト音量 80%
  },
  textGeneration: DEFAULT_TEXT_GENERATION_SETTINGS,
  thumbnail: { enabled: true, duration: 2, sfxEnabled: true, ttsSpeed: 1.2, maxTextChars: 13, bgmInThumbnail: false, ttsMatchDuration: true, useDedicatedModel: true },
  tastePresetOverrides: {},
  // systemPrompts is not included in DEFAULT_SETTINGS intentionally;
  // loadAccountSettings() deep-merges DEFAULT_SYSTEM_PROMPTS as fallback
}

// Settings update request (partial)
export type SettingsUpdate = {
  [K in keyof AppSettings]?: Partial<AppSettings[K]>
}

/**
 * Get CRF value for quality preset
 * Based on Remotion best practices for H.264 encoding
 *
 * CRF Range: 1-51 (lower = better quality, higher = smaller file)
 * - H.264 Default: 18 (Remotion recommended)
 * - Acceptable range: 15-28
 */
export function getCRFForPreset(preset: VideoQualityPreset, codec: VideoCodec = 'h264'): number {
  if (codec === 'h264') {
    switch (preset) {
      case 'draft':
        return 28 // Fast encoding, smaller files, lower quality
      case 'standard':
        return 23 // Balanced quality/size
      case 'high':
        return 18 // Remotion default, high quality
      case 'ultra':
        return 15 // Maximum quality, large files
      case 'custom':
        return 18 // Default to high quality for custom
    }
  } else if (codec === 'h265') {
    switch (preset) {
      case 'draft':
        return 32
      case 'standard':
        return 28
      case 'high':
        return 23 // H.265 default
      case 'ultra':
        return 20
      case 'custom':
        return 23
    }
  } else if (codec === 'vp9') {
    switch (preset) {
      case 'draft':
        return 35
      case 'standard':
        return 31
      case 'high':
        return 28 // VP9 default
      case 'ultra':
        return 25
      case 'custom':
        return 28
    }
  }

  // Fallback to H.264 high quality
  return 18
}

// ============================================
// テイストプリセット
// ============================================

/**
 * テイストプリセットID
 */
export type TastePresetId =
  | 'default'           // デフォルト（現在のまま）
  | 'elegant'           // エレガント
  | 'minimalist'        // ミニマリズム
  | 'chic'              // シック
  | 'pop'               // ポップ
  | 'gorgeous'          // ゴージャス
  | 'premium'           // プレミアム（高級感、品質重視）
  | 'modern'            // モダン（現代的、先進的）
  | 'classic'           // クラシック（伝統的、格調高い）
  | 'industrial'        // インダストリアル（工業的、無骨）
  | 'monochrome'        // モノクロ（白黒、ミニマル）
  | 'pastel'            // パステル（柔らかい色合い）
  | 'earth'             // アース（大地、自然色）
  | 'wa'                // 和（和風、日本的）
  | 'mediterranean'     // 地中海（地中海風、明るい）
  | 'bohemian'          // ボヘミアン（自由奔放、民族的）
  | 'vintage'           // ヴィンテージ（古き良き時代）
  | 'futuristic'        // フューチャーリスティック（未来的、SF）
  | 'fresh'             // フレッシュ（爽やか、新鮮）
  | 'natural'           // ナチュラル（自然、田舎風）
  | 'stylish'           // スタイリッシュ（都会的、洗練）
  | 'girly'             // ガーリー（可愛らしい、女性的）
  | 'funny'             // ファニー（楽しい、ユーモラス）
  | 'kids'              // キッズ（子供向け）
  | 'baby'              // ベイビー（赤ちゃん・乳幼児向け）
  | 'nordic'            // 北欧テイスト
  | 'retro'             // あえてださめ（レトロ）
  | 'handmade'          // 温かみのある手作り感（クラフト）
  // ファッション系（FashionSlide用）
  | 'fashion-minimal'   // ミニマルホワイト（UNIQLO/無印風）
  | 'fashion-dark'      // ダーク＆ムーディ（ハイブランド風）
  | 'fashion-editorial' // エディトリアル（Vogue風）
  | 'fashion-street'    // ストリート（Nike風）
  | 'fashion-girly'     // フェミニンエレガンス（楽天30-40代女性向け）
  | 'fashion-luxury'    // ラグジュアリー（Gucci風）

/**
 * テイストプリセットのカテゴリー
 */
export type TastePresetCategory = 'standard' | 'lifestyle' | 'target' | 'creative' | 'fashion'

/**
 * テイストプリセットのメタ情報
 */
export interface TastePresetMeta {
  id: TastePresetId
  name: string              // 日本語表示名
  description: string       // 説明文
  category: TastePresetCategory
  icon?: string             // アイコン絵文字（オプション）

  // === NEW: マッチング用メタデータ ===
  targetAudience?: {
    ageGroups?: string[]        // ['20s', '30s']
    gender?: string[]           // ['female', 'unspecified']
    occupation?: string[]       // ['professional', 'student']
    lifestyle?: string[]        // ['urban', 'active']
  }
  suitableFor?: {
    productCategories?: string[] // ['cosmetics', 'fashion']
    useCases?: string[]          // ['daily-use', 'premium']
    seasons?: string[]           // ['spring', 'summer']
  }
  concepts?: string[]           // ['luxury', 'elegant']
  emotions?: string[]           // ['calm', 'confident']
}

/**
 * テイストプリセットの適用設定（部分的）
 * すべてのフィールドはオプショナル - 指定したもののみ上書き
 */
export interface TastePresetSettings {
  // トランジション
  transitions?: Partial<SimpleSlideTemplateSettings['transitions']>

  // スタイル（3種類）
  headlineStyle?: Partial<HeadlineStyle>
  sellingPointStyle?: Partial<SellingPointStyle>
  ctaStyle?: Partial<CTAStyle>

  // 新フォーマット（優先、部分的な定義を許可）
  sceneSettings?: Partial<Record<SceneKey, SceneSettingsItem>>

  // 背景色
  mainBackgroundColor?: string

  // フォントサイズ（オプション、通常は変更しない）
  fontSizes?: Partial<SimpleSlideTemplateSettings['fontSizes']>

  // PASONAフェーズ別テキストスタイル（将来プリセット側で個別定義できるよう型だけ追加）
  pasonaAttentionStyle?: Partial<HeadlineStyle>
  pasonaAgitationStyle?: Partial<HeadlineStyle>
  pasonaSolutionStyle?: Partial<HeadlineStyle>
  pasonaOfferStyle?: Partial<HeadlineStyle>
  pasonaNarrowingStyle?: Partial<HeadlineStyle>
}

/**
 * 完全なテイストプリセット定義
 */
export interface TastePreset {
  meta: TastePresetMeta
  settings: TastePresetSettings
}
